import { Invoice } from '@/types/payments';
import { PaymentGroup } from '@/types/paymentConfirmation';
import { BankAccountSummary } from '@/types/accounts';
import type { BankConstraintInfo } from '@/types/bankConstraints';
import { captureError } from '@/lib/observability';

const NO_SOURCE_ACCOUNT_CHOSEN = 'No source account chosen';

function warnUnresolvedSource(invoice: Invoice, sourceAccount: BankAccountSummary | undefined): void {
  if (!invoice.sourceAccountId) {
    console.warn('No source account chosen for invoice:', invoice.id);
  } else if (!sourceAccount) {
    console.warn('Source account not found for invoice:', invoice.id, 'sourceAccountId:', invoice.sourceAccountId);
  } else {
    console.warn('Source account missing BIC, skipping grouping for invoice:', invoice.id);
  }
}

function addToUnresolvableGroup(
  groups: Record<string, PaymentGroup>,
  ibanSets: Record<string, Set<string>>,
  invoice: Invoice,
  sourceAccount: BankAccountSummary | undefined,
): void {
  // `||`, not `??`: an unchosen source arrives as an empty string from the payables mapper.
  const unresolvedKey = `unresolvable:${invoice.sourceAccountId || 'unresolvable'}`;
  if (!groups[unresolvedKey]) {
    groups[unresolvedKey] = {
      id: unresolvedKey,
      bankAccount: invoice.sourceAccountId || 'unresolvable',
      bankName: sourceAccount?.bankName ?? (invoice.sourceAccountId ? undefined : NO_SOURCE_ACCOUNT_CHOSEN),
      sourceAccounts: [],
      sourceIbans: [],
      invoices: [],
      status: 'pending',
      totalsByCurrency: {},
      bicFi: undefined,
    };
    ibanSets[unresolvedKey] = new Set<string>();
  }

  const group = groups[unresolvedKey];
  const unresolvedSourceAccountId = invoice.sourceAccountId || '';
  if (unresolvedSourceAccountId && !group.sourceAccounts.includes(unresolvedSourceAccountId)) {
    group.sourceAccounts.push(unresolvedSourceAccountId);
  }

  group.invoices.push(invoice);
  if (!group.totalsByCurrency) group.totalsByCurrency = {};
  group.totalsByCurrency[invoice.currency] = (group.totalsByCurrency[invoice.currency] || 0) + invoice.amount;
}

/**
 * A payment needs an amount. A finalized payable always has one (the financial_documents CHECK
 * requires it), so one without is a data fault: it is left out of signing, never paid as 0, and
 * reported (FIN-413).
 */
function withPayableAmounts(invoices: Invoice[]): Invoice[] {
  const withAmount = invoices.filter((invoice) => invoice.amount !== null && invoice.amount !== undefined);
  if (withAmount.length !== invoices.length) {
    const documentIds = invoices.filter((invoice) => !withAmount.includes(invoice)).map((invoice) => invoice.id);
    captureError(
      new Error(`PAYMENT_AMOUNT_MISSING: ${documentIds.length} payable(s) without an amount left out of signing (FIN-413): ${documentIds.join(', ')}`),
      'paymentGrouping: PAYMENT_AMOUNT_MISSING',
    );
  }
  return withAmount;
}

export function groupInvoicesByBankAndCurrency(invoices: Invoice[], bankAccounts: BankAccountSummary[]): PaymentGroup[] {
  const groups: Record<string, PaymentGroup> = {};

  // Track unique IBANs per BIC using Sets
  const ibanSets: Record<string, Set<string>> = {};

  withPayableAmounts(invoices).forEach((invoice) => {
    // No sourceAccountId means no account was chosen (FIN-968 E1c): it is never grouped under
    // some other account, whose IBAN would then be shown as the payment's source.
    const sourceAccount = invoice.sourceAccountId
      ? bankAccounts.find(acc => acc.id === invoice.sourceAccountId)
      : undefined;

    // An invoice whose source account can't be resolved (unset or unknown sourceAccountId) or
    // whose resolved account has no BIC still needs to be accounted for in the output — see
    // issue #822. Route it into its own bucket instead of dropping it, keyed by its
    // sourceAccountId (or a shared 'unresolvable' bucket when there is none to key on).
    if (!sourceAccount || !sourceAccount.bic) {
      warnUnresolvedSource(invoice, sourceAccount);
      addToUnresolvableGroup(groups, ibanSets, invoice, sourceAccount);
      return;
    }

    const bic = sourceAccount.bic;
    const sourceBankName = sourceAccount.bankName;
    const sourceIban = sourceAccount.accountNumber; // This is the IBAN
    const currency = invoice.currency;
    const key = bic; // Group by BIC only

    if (!groups[key]) {
      groups[key] = {
        id: key,
        bankAccount: bic, // Use BIC as the main identifier
        bankName: sourceBankName,
        sourceAccounts: [],
        sourceIbans: [],
        invoices: [],
        status: 'pending',
        totalsByCurrency: {},
        bicFi: bic
      };
      ibanSets[key] = new Set<string>();
    }

    // Track all unique source account IDs in this BIC group
    const sourceAccountId = invoice.sourceAccountId || '';
    if (sourceAccountId && !groups[key].sourceAccounts.includes(sourceAccountId)) {
      groups[key].sourceAccounts.push(sourceAccountId);
    }

    // Track all unique IBANs in this BIC group using Set
    if (sourceIban) {
      ibanSets[key].add(sourceIban);
    }

    groups[key].invoices.push(invoice);
    if (!groups[key].totalsByCurrency) groups[key].totalsByCurrency = {};
    groups[key].totalsByCurrency[currency] = (groups[key].totalsByCurrency[currency] || 0) + invoice.amount;
  });

  // Convert Sets to arrays
  Object.keys(groups).forEach(bic => {
    groups[bic].sourceIbans = Array.from(ibanSets[bic]);
  });

  return Object.values(groups);
}

/**
 * Splits any payment group whose invoice count exceeds its bank's maxBasketSize
 * into sequential chunks, each within the limit. Groups with no constraint or
 * a null maxBasketSize are returned unchanged.
 */
export function splitGroupsByBasketLimit(
  groups: PaymentGroup[],
  constraintByBic: Record<string, BankConstraintInfo | null>
): PaymentGroup[] {
  return groups.flatMap(group => {
    const bic = group.bicFi ?? group.bankAccount;
    const limit = constraintByBic[bic]?.maxBasketSize ?? null;
    if (limit === null || group.invoices.length <= limit) return [group];

    const chunks: PaymentGroup[] = [];
    for (let i = 0; i < group.invoices.length; i += limit) {
      const slice = group.invoices.slice(i, i + limit);
      const partIndex = Math.floor(i / limit) + 1;
      const totalParts = Math.ceil(group.invoices.length / limit);
      const totalsByCurrency: Record<string, number> = {};
      slice.forEach(inv => {
        totalsByCurrency[inv.currency] = (totalsByCurrency[inv.currency] || 0) + inv.amount;
      });
      chunks.push({
        ...group,
        id: `${group.id}-chunk-${partIndex}`,
        invoices: slice,
        totalsByCurrency,
        splitInfo: { partIndex, totalParts, bankLimit: limit },
      });
    }
    return chunks;
  });
}
